import dayjs, { Dayjs } from 'dayjs';
import timezone from 'dayjs/plugin/timezone';
import utc from 'dayjs/plugin/utc';
import { Timezone } from '@/modules/common/constants/meeting-timezone.ts';

dayjs.extend(utc);
dayjs.extend(timezone);

const UI_DATE_TIME_FORMAT = 'YYYY-MM-DD HH:mm:ss.SSS';
const API_DATE_TIME_FORMAT = 'YYYY-MM-DDTHH:mm:ss.SSSZ';

interface UiDateTimeToMeetingParams {
  datePart: Dayjs;
  timePart: Dayjs;
  timeZone: string;
}

const buildUiLocalDateTimeString = ({ datePart, timePart }: Omit<UiDateTimeToMeetingParams, 'timeZone'>) =>
  `${datePart.format('YYYY-MM-DD')} ${timePart.format('HH:mm:ss.SSS')}`;

const getNormalizedTimeZone = (timeZone?: string | null) => timeZone || Timezone['Australia/Sydney'];

export const uiDateTimeToUtcIso = ({ datePart, timePart, timeZone }: UiDateTimeToMeetingParams) =>
  dayjs
    .tz(
      buildUiLocalDateTimeString({ datePart, timePart }),
      UI_DATE_TIME_FORMAT,
      getNormalizedTimeZone(timeZone),
    )
    .utc()
    .format(API_DATE_TIME_FORMAT);

export const meetingDateTimeToUtcIso = (meetingDateTime: Dayjs, timeZone: string) =>
  dayjs
    .tz(meetingDateTime.format(UI_DATE_TIME_FORMAT), UI_DATE_TIME_FORMAT, getNormalizedTimeZone(timeZone))
    .utc()
    .format(API_DATE_TIME_FORMAT);

export const uiDateTimeToRecurrenceStartDate = ({
  datePart,
  timePart,
  timeZone,
}: UiDateTimeToMeetingParams) =>
  dayjs
    .tz(
      buildUiLocalDateTimeString({ datePart, timePart }),
      UI_DATE_TIME_FORMAT,
      getNormalizedTimeZone(timeZone),
    )
    .format('YYYY-MM-DD');

export const utcIsoToMeetingDateTime = (utcIso: string, timeZone?: string | null) =>
  dayjs.utc(utcIso).tz(getNormalizedTimeZone(timeZone));

export const getNowInMeetingTimeZone = (timeZone?: string | null) =>
  dayjs().tz(getNormalizedTimeZone(timeZone));

export const meetingDateBoundaryToUtcDate = (
  date: string,
  timeZone: string,
  boundary: 'start' | 'end',
) => {
  const time = boundary === 'start' ? '00:00:00.000' : '23:59:59.999';
  return dayjs
    .tz(`${date} ${time}`, UI_DATE_TIME_FORMAT, getNormalizedTimeZone(timeZone))
    .toDate();
};
